import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface CustomerKioskSession {
  accountId: string;
  organizationId: string;
  customerId: string | null;
  username: string;
  customerNumber: string;
  firstName: string | null;
  lastName: string | null;
  walletBalance: number;
  sessionStartedAt: number;
}

const CUSTOMER_KIOSK_SESSION_KEY = 'blaze_customer_kiosk_session';
const SESSION_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes of inactivity

export function useCustomerKiosk(organizationId: string | null) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<CustomerKioskSession | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Use ref to always have current organizationId in mutation closures
  const orgIdRef = useRef(organizationId);
  orgIdRef.current = organizationId;

  // Load session from localStorage on mount
  useEffect(() => {
    // Don't initialize until we have an organization ID
    if (!organizationId) {
      return;
    }

    try {
      const stored = localStorage.getItem(CUSTOMER_KIOSK_SESSION_KEY);
      
      if (stored) {
        const parsed = JSON.parse(stored) as CustomerKioskSession;
        
        const orgMatch = parsed.organizationId === organizationId;
        const notExpired = Date.now() - parsed.sessionStartedAt < SESSION_TIMEOUT_MS;
        
        // Check if session is for the same org and not expired
        if (orgMatch && notExpired) {
          setSession(parsed);
        } else {
          // Clear expired or wrong-org session
          localStorage.removeItem(CUSTOMER_KIOSK_SESSION_KEY);
        }
      }
    } catch (e) {
      console.error('Error loading session:', e);
      localStorage.removeItem(CUSTOMER_KIOSK_SESSION_KEY);
    }
    setIsInitialized(true);
  }, [organizationId]);

  // Save session to localStorage when it changes
  useEffect(() => {
    if (session) {
      localStorage.setItem(CUSTOMER_KIOSK_SESSION_KEY, JSON.stringify(session));
    }
  }, [session]);

  // Login with username and password
  const loginWithPassword = useMutation({
    mutationFn: async ({ username, password }: { username: string; password: string }) => {
      const currentOrgId = orgIdRef.current;
      if (!currentOrgId) throw new Error('Organization not configured');

      // Authenticate using server-side function
      const { data, error } = await supabase.rpc('authenticate_customer_password', {
        p_organization_id: currentOrgId,
        p_username: username,
        p_password: password,
      });

      if (error) throw error;

      const result = data?.[0];
      if (!result?.success) {
        throw new Error(result?.error_message || 'Invalid username or password');
      }

      const newSession: CustomerKioskSession = {
        accountId: result.account_id,
        organizationId: currentOrgId,
        customerId: result.customer_id,
        username: result.username,
        customerNumber: result.customer_number,
        firstName: result.first_name,
        lastName: result.last_name,
        walletBalance: Number(result.wallet_balance),
        sessionStartedAt: Date.now(),
      };

      // Save to localStorage BEFORE setting state (navigation happens right after)
      localStorage.setItem(CUSTOMER_KIOSK_SESSION_KEY, JSON.stringify(newSession));
      
      setSession(newSession);
      return newSession;
    },
  });

  // Login with customer number and PIN
  const loginWithPin = useMutation({
    mutationFn: async ({ customerNumber, pin }: { customerNumber: string; pin: string }) => {
      const currentOrgId = orgIdRef.current;
      if (!currentOrgId) throw new Error('Organization not configured');

      // Authenticate using server-side function
      const { data, error } = await supabase.rpc('authenticate_customer_pin', {
        p_organization_id: currentOrgId,
        p_customer_number: customerNumber,
        p_pin: pin,
      });

      if (error) throw error;

      const result = data?.[0];
      if (!result?.success) {
        throw new Error(result?.error_message || 'Invalid customer number or PIN');
      }

      const newSession: CustomerKioskSession = {
        accountId: result.account_id,
        organizationId: currentOrgId,
        customerId: result.customer_id,
        username: result.username,
        customerNumber: result.customer_number,
        firstName: result.first_name,
        lastName: result.last_name,
        walletBalance: Number(result.wallet_balance),
        sessionStartedAt: Date.now(),
      };

      // Save to localStorage BEFORE setting state (navigation happens right after)
      localStorage.setItem(CUSTOMER_KIOSK_SESSION_KEY, JSON.stringify(newSession));
      
      setSession(newSession);
      return newSession;
    },
  });

  // Refresh wallet balance
  const refreshBalance = useCallback(async () => {
    if (!session) return null;

    const { data, error } = await supabase.rpc('get_wallet_balance', {
      p_customer_account_id: session.accountId,
    });

    if (error) {
      console.error('Failed to refresh balance:', error);
      return null;
    }

    const newBalance = Number(data);
    setSession((prev) =>
      prev ? { ...prev, walletBalance: newBalance, sessionStartedAt: Date.now() } : null
    );

    return newBalance;
  }, [session]);

  // Update balance locally (used after payment)
  const updateBalance = useCallback((newBalance: number) => {
    setSession((prev) =>
      prev ? { ...prev, walletBalance: newBalance, sessionStartedAt: Date.now() } : null
    );
  }, []);

  // Extend session (reset timeout)
  const extendSession = useCallback(() => {
    setSession((prev) =>
      prev ? { ...prev, sessionStartedAt: Date.now() } : null
    );
  }, []);

  // Logout
  const logout = useCallback(() => {
    setSession(null);
    localStorage.removeItem(CUSTOMER_KIOSK_SESSION_KEY);
    queryClient.clear();
  }, [queryClient]);

  // Check if session is expired
  const isSessionExpired = useCallback(() => {
    if (!session) return true;
    return Date.now() - session.sessionStartedAt >= SESSION_TIMEOUT_MS;
  }, [session]);

  return {
    session,
    isAuthenticated: !!session && !isSessionExpired(),
    isInitialized,
    loginWithPassword,
    loginWithPin,
    refreshBalance,
    updateBalance,
    extendSession,
    logout,
    isSessionExpired,
  };
}

// Hook for processing wallet payments
export function useWalletPayment() {
  const queryClient = useQueryClient();

  const processPayment = useMutation({
    mutationFn: async ({
      customerAccountId,
      orderId,
      amount,
    }: {
      customerAccountId: string;
      orderId: string;
      amount: number;
    }) => {
      const { data, error } = await supabase.rpc('process_wallet_payment', {
        p_customer_account_id: customerAccountId,
        p_order_id: orderId,
        p_amount: amount,
      });

      if (error) throw error;

      const result = data?.[0];
      if (!result?.success) {
        throw new Error(result?.message || 'Payment failed');
      }

      return {
        success: true,
        newBalance: Number(result.new_balance),
        transactionId: result.transaction_id,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
    },
  });

  return {
    processPayment,
    isProcessing: processPayment.isPending,
  };
}

// Check if string is a UUID
function isUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

// Fetch organization info for kiosk branding (accepts UUID or slug)
export function useKioskOrganization(orgIdentifier: string | null) {
  return useQuery({
    queryKey: ['kiosk-organization', orgIdentifier],
    queryFn: async () => {
      if (!orgIdentifier) return null;

      // Determine if we're looking up by UUID or slug
      const lookupField = isUUID(orgIdentifier) ? 'id' : 'slug';

      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, slug, logo_url, currency, timezone')
        .eq(lookupField, orgIdentifier)
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error(`Organization not found: ${orgIdentifier}`);
      return data;
    },
    enabled: !!orgIdentifier,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on org not found
  });
}

// Fetch location info for kiosk (accepts UUID or code)
// When using code, organizationId is required for uniqueness
export function useKioskLocation(locIdentifier: string | null, organizationId?: string | null) {
  return useQuery({
    queryKey: ['kiosk-location', locIdentifier, organizationId],
    queryFn: async () => {
      if (!locIdentifier) return null;

      // If it's a UUID, lookup directly by ID
      if (isUUID(locIdentifier)) {
        const { data, error } = await supabase
          .from('locations')
          .select('id, name, code, organization_id, default_tax_rate')
          .eq('id', locIdentifier)
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        return data;
      }

      // Otherwise, lookup by code within organization
      if (!organizationId) {
        throw new Error('Organization ID required when looking up by location code');
      }

      const { data, error } = await supabase
        .from('locations')
        .select('id, name, code, organization_id, default_tax_rate')
        .eq('code', locIdentifier)
        .eq('organization_id', organizationId)
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!locIdentifier && (isUUID(locIdentifier) || !!organizationId),
    staleTime: 5 * 60 * 1000,
  });
}

